/* External */
import { useCallback, useState, useEffect } from 'react';

/* Providers */
import { useUserAccount } from '../providers/UserProvider';

const OLD_KEY = 'audioAndSubtiles';
const KEY = 'audioAndSubtitlesPreference';

let currentPreference;

export function updateAudioAndSubtitlesPreference(userId, preference) {
  if (!preference || !userId) return;
  const newPreference = {
    userId,
    ...currentPreference,
    ...preference,
  };
  currentPreference = newPreference;
  localStorage.setItem(KEY, JSON.stringify(currentPreference));
}

export function getAudioAndSubtitlesPreference(userId) {
  currentPreference = currentPreference || JSON.parse(localStorage.getItem(KEY));
  if (!currentPreference) {
    const oldPreference = JSON.parse(localStorage.getItem(OLD_KEY));
    if (oldPreference) {
      currentPreference = {
        userId: oldPreference.prevUserUid,
        subtitles: {
          iso: oldPreference.subtitleTrack,
          kind: oldPreference.subtitleTrackKind,
        },
        audio: {
          iso: oldPreference.audioTrack,
          isDolby: false,
          isDescriptive: false,
        },
      };
      updateAudioAndSubtitlesPreference(userId, currentPreference);
    }
    localStorage.removeItem(OLD_KEY);
  }
  if (currentPreference?.userId !== userId) {
    updateAudioAndSubtitlesPreference(userId, {});
  }
  return currentPreference;
}

export function useAudioAndSubtitlesPreference() {
  const { uid } = useUserAccount() || {};
  const [preference, setPreference] = useState(() => getAudioAndSubtitlesPreference(uid));

  const updatePreference = useCallback((newPreference) => {
    setPreference((current) => ({
      ...current,
      ...newPreference,
    }));
    updateAudioAndSubtitlesPreference(uid, newPreference);
  }, [uid]);

  const saveAudioPreference = useCallback(({ iso, isDolby, quality: { isDescriptive } }) => {
    updatePreference({
      audio: {
        iso,
        isDolby,
        isDescriptive,
      },
    });
  }, [updatePreference]);

  const saveSubtitlesPreference = useCallback((subtitleTrack) => {
    updatePreference({
      subtitles: subtitleTrack ? {
        iso: subtitleTrack.iso,
        kind: subtitleTrack.kind,
      } : undefined,
    });
  }, [updatePreference]);

  useEffect(() => {
    setPreference(getAudioAndSubtitlesPreference(uid));
  }, [uid]);

  return {
    preference,
    saveAudioPreference,
    saveSubtitlesPreference,
  };
}

export function getMatchingAudio(audioPreference, audioTracks) {
  return audioTracks.find((track) => (
    track.iso === audioPreference.iso
      && track.isDolby === audioPreference.isDolby
      && track.quality.isDescriptive === audioPreference.isDescriptive
  ));
}

export function getMatchingSubtitle(subtitlesPreference, subtitleTracks) {
  return subtitleTracks.find((track) => (
    track.iso === subtitlesPreference.iso
      && track.kind === subtitlesPreference.kind
  ));
}

export function hasAudioTrackByLanguage(audioTracks, language) {
  return audioTracks.some((audio) => audio.iso === language);
}

export function getSubtitleTrackByLanguage(subtitleTracks, language) {
  return subtitleTracks.find((subtitle) => subtitle.iso === language);
}
